import classNames from 'classnames';
import _, { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import TimeTotalField from '../TimeField/TimeTotalField';
import './TotalField.scss';
import ITotalFieldProps from './TotalFieldProps';

const TotalField = ({
  value,
  input,
  minuteStep,
  disabled,
  placeholder,
  error,
  onChange,
  onFocus,
  onBlur,
  className,
  maxHours,
  minHours,
}: ITotalFieldProps) => {
  const [inputValue, setInputValue] = useState<string>(input ? input : '');
  const [hasError, setHasError] = useState(false);
  const [inputIsDirty, setInputIsDirty] = useState(false);
  const [currentValue, setCurrentValue] = useState<number>();

  function secondsToHHmmString(value: number) {
    const h = Math.floor(value / 3600);
    const m = Math.floor((value % 3600) / 60);
    const hDisplay = h > 0 ? (h < 10 ? h.toString() : h.toString()) : '0';
    const mDisplay = m > 0 ? (m < 10 ? '0' + m.toString() : m.toString()) : '00';
    return hDisplay + ':' + mDisplay;
  }

  useEffect(() => {
    if (!isNil(value)) {
      // Need to take the value that's passed, check the input value's parsed value and make sure they're not the same before auto filling
      const formattedCurrent = format(inputValue);
      if (formattedCurrent) {
        const currentInputValueValue = inputValue ? toMilitaryTime(formattedCurrent) : null;
        if (currentInputValueValue !== secondsToHHmmString(value)) {
          const timeString = secondsToHHmmString(value);
          setInputValue(timeString);
        }
      } else {
        const timeString = secondsToHHmmString(value);
        setInputValue(timeString);
      }
      setCurrentValue(value);
    } else {
      // tslint:disable-next-line: no-console
      console.error('Value for total field must be a valid number or null');
      // setHasError(true);
      setInputIsDirty(true);
    }
  }, [value]);

  useEffect(() => {
    if (input) {
      handleInputChange(input);
    }
  }, [input]);

  function handleChange(val: string | null) {
    let formatted = null;
    const isValid = val !== null && !_.isEmpty(val);

    let newValue: number | null = value;
    if (isValid && val) {
      formatted = val;
      if (isValid && currentValue !== undefined) {
        newValue = getSecondsFromString(val);
        setCurrentValue(newValue!);
      } else {
        newValue = null;
      }
    } else {
      newValue = null;
    }

    const trimmed = formatted?.trim() ?? null;

    onChange({
      value: isValid ? (newValue !== null && newValue <= 0 ? 1 : newValue) : null,
      formatted: trimmed,
      isValid: newValue !== null,
    });

    if (trimmed && isValidInputText(trimmed)) {
      setHasError(false);
    }
  }

  function getSecondsFromString(val: string) {
    const split = val.split(':');
    return +split[0] * 60 * 60 + +split[1] * 60;
  }

  function handleInputChange(val: string) {
    let newValue = null;
    newValue = toMilitaryTime(format(val)!);
    setInputValue(val);
    handleChange(newValue);
  }

  // Add num hours to state.value
  function adjustHours(num: number) {
    // tslint:disable-next-line: prefer-const
    const standardTime = parseStandardTime(value === null ? '' : secondsToHHmmString(value));
    const { minutes } = standardTime;
    let { hours } = standardTime;

    hours += num;
    if (hours <= 0) {
      setInputValue('0:00');
      handleChange('0:00');
      return;
    }

    if (maxHours && hours > maxHours) {
      return;
    }

    if (minHours && hours < minHours) {
      return;
    }

    const hoursString = hours;
    const minutesString = addLeadingZeros(minutes);
    const val = hoursString + ':' + minutesString;

    setInputValue(val);
    handleChange(val);
  }

  function adjustMinutes(num: number) {
    // tslint:disable-next-line: prefer-const
    const standardTime = parseStandardTime(value === null ? '' : secondsToHHmmString(value));
    const { hours } = standardTime;
    let { minutes } = standardTime;

    minutes += num;
    if (minutes >= 60) {
      minutes -= 60;
    } else if (minutes < 0) {
      minutes += 60;
    }

    const hoursStr = hours;
    const minutesStr = addLeadingZeros(minutes);
    const val = hoursStr + ':' + minutesStr;

    setInputValue(val);
    handleChange(val);
  }

  function addLeadingZeros(num: number) {
    const numStr = num.toString();
    return numStr.length < 2 ? '0' + numStr : numStr;
  }

  function isValidInputText(str: string) {
    return format(str) !== null;
  }

  // parses time string to an objects containing hours and minutes
  function parseStandardTime(timeStr: string) {
    const result = {
      hours: 0,
      minutes: 0,
    };

    if (isHoursMinutesFormat(timeStr)) {
      const split = timeStr.split(':');
      result.hours = parseInt(split[0], 10);
      result.minutes = parseInt(split[1], 10);
    }
    return result;
  }

  function isHoursMinutesFormat(val: string | null) {
    if (val === null) {
      return false;
    }
    return val.match(/^\d+:[0-9][0-9]$/) !== null;
  }

  // Formats the number string by adding the colon and making assumptions about
  // missing digits (i.e. 8 == 8:00). Returns false if the string can't be formatted.
  function format(timeStr: string, allowLeadingZeroes = false) {
    let res: string | null = allowLeadingZeroes ? timeStr.slice() : timeStr.replace(/^0+/, ''); // remove leading zeros
    if (!isHoursMinutesFormat(res)) {
      if (/^[1-9]{1}$/.test(res) || /^1{1}[0-9]{1}$/.test(res) || /^2{1}[0-4]{1}$/.test(res)) {
        // 1 - 9 hours or 10 - 19 hours or 20 -24 hours
        res = `${res}:00`;
      } else if (/^[1-9]{1}[0-9]{1}$/.test(res)) {
        res = res.slice(0, 1) + ':' + res.slice(1) + '0';
      } else if (/^[1-9]{1}[0-5]{1}[0-9]$/.test(res)) {
        // 3 numbers
        res = res.slice(0, 1) + ':' + res.slice(1);
      } else if (/^1[0-2]{1}[0-5]{1}[0-9]{1}$/.test(res)) {
        // 4 numbers
        res = res.slice(0, 2) + ':' + res.slice(2);
      } else if (/^1[0-2]{1}[0]{1,4}$/.test(res)) {
        // trailing zeros (10, 11, 12)
        res = res.slice(0, 2) + ':00';
      } else if (/^[1-9]{1}[0]{1,4}$/.test(res)) {
        // trailing zeros
        res = res.slice(0, 1) + ':00';
      } else if (/^:[0-5]{1}[0-9]{1}$/.test(res)) {
        // :30 => 0:30
        res = '0' + res;
      } else if (/^:[0-5]{1}$/.test(res)) {
        // :3 => 0:30
        res = '0' + res + '0';
      }

      if (!isHoursMinutesFormat(res)) {
        res = null;
      }
    }
    return res;
  }

  function toMilitaryTime(standardTime: string) {
    if (standardTime && standardTime.includes(':')) {
      const split = standardTime.split(':');
      const hours = +split[0];
      const militaryTime = hours + ':' + split[1].split(' ')[0];
      return militaryTime;
    }
    return standardTime;
  }

  function handleOnChange(val: string | null) {
    if (val !== null) {
      handleInputChange(val);
    }
  }

  function handleInputBlur(val: string) {
    if (isValidInputText(val)) {
      // strip leading zeroes, then re-pad if necessary
      let paddedVal = val.replace(/^0+/, '');
      if (paddedVal.charAt(0) === ':') {
        paddedVal = `0${paddedVal}`;
      }

      const formatted = format(paddedVal, false);
      if (formatted) {
        setInputValue(formatted.trim());
      }
    } else {
      setHasError(true);
    }

    setInputIsDirty(true);

    if (onBlur) {
      onBlur();
    }
  }

  const classes = classNames('time-field', className);

  return (
    <div className={classes}>
      <TimeTotalField
        tabIndex={0}
        value={inputValue}
        disabled={disabled}
        placeholder={placeholder}
        minuteStep={minuteStep}
        adjustMinutes={adjustMinutes}
        adjustHours={adjustHours}
        onFocus={onFocus}
        onBlur={handleInputBlur}
        error={(inputIsDirty && hasError) || error}
        handleChange={handleOnChange}
        inputs={{ inputValue: input }}
      />
    </div>
  );
};

TotalField.defaultProps = {
  value: '',
  minuteStep: 5,
  disabled: false,
  placeholder: 'H:MM',
};

export default TotalField;
